import React, { useMemo, useState, useEffect } from "react";
import { Save, X, Plus, Trash, Printer } from "lucide-react";
import { printMovement } from "../print/printMovement";
import { PhotoEvidenceModule } from "../attachments/PhotoEvidenceModule";
import { api } from "../../services/api";

export function ConsumiblesModule({ materials, brandItems = [], warehouses = [
  { id: 'popayan', name: 'Bodega Popayán' },
  { id: 'santander', name: 'Bodega Santander' }
], people = [], onRegister }) {
  const [exitType, setExitType] = useState("Consumible");
  const [warehouseId, setWarehouseId] = useState(warehouses[0]?.id || 'popayan');
  const [materialId, setMaterialId] = useState(materials[0]?.id || "");
  const [quantity, setQuantity] = useState(0);
  const [reason, setReason] = useState("");
  const [observations, setObservations] = useState("");
  const [serialOption, setSerialOption] = useState('No');
  const [serial, setSerial] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('');
  const [items, setItems] = useState([]);
  const [statusMsg, setStatusMsg] = useState(null);
  const [lastReceipt, setLastReceipt] = useState(null);
  const [responsibleId, setResponsibleId] = useState("");
  const [evidencePhotos, setEvidencePhotos] = useState([]);
  // Fase 3 del puente Almacén–Compras: centro de costo del consumo, para
  // poder reportar cuánto consume cada área mes a mes.
  const [costCenters, setCostCenters] = useState([]);
  const [costCenter, setCostCenter] = useState("");

  const exitTypes = ["Consumible"];

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await api.accounting.costCenters.list({ limit: 2000, order: 'asc' });
        const rows = Array.isArray(res?.data) ? res.data : [];
        if (active) setCostCenters(rows);
      } catch (_) {
        if (active) setCostCenters([]);
      }
    })();
    return () => { active = false; };
  }, []);

  const filteredMaterials = useMemo(() => {
    if (!warehouseId) return [];
    return materials.filter(m => {
      const qty = m.stockByWarehouse?.[warehouseId];
      return qty && Number(qty) > 0;
    }).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [materials, warehouseId]);

  useEffect(() => {
    if (materialId && !filteredMaterials.find(m => String(m.id) === String(materialId))) {
      setMaterialId("");
    }
  }, [warehouseId, filteredMaterials, materialId]);

  const selectedMaterial = useMemo(() => {
    return materials.find(m => String(m.id) === String(materialId));
  }, [materials, materialId]);

  const brandOptions = useMemo(() => {
    const code = selectedMaterial?.code || '';
    const type = selectedMaterial?.type || 'Material';
    return brandItems.filter(b => (b.materialCode === code || !b.materialCode) && (b.materialType === type || !b.materialType));
  }, [brandItems, selectedMaterial]);

  const canSubmitSingle = useMemo(() => {
    const requires = !!selectedMaterial?.requiresSerial;
    const serialValid = requires ? serialOption === 'Sí' && !!serial.trim() : true;
    return warehouseId && materialId && Number(quantity) > 0 && exitType && responsibleId && serialValid && !!costCenter;
  }, [warehouseId, materialId, quantity, exitType, responsibleId, serialOption, serial, selectedMaterial, costCenter]);

  const handleCancel = () => {
    setExitType("Consumible");
    setWarehouseId(warehouses[0]?.id || 'popayan');
    setMaterialId(materials[0]?.id || "");
    setQuantity(0);
    setReason("");
    setObservations("");
    setSerialOption('No');
    setSerial('');
    setSelectedBrand('');
    setResponsibleId("");
    setEvidencePhotos([]);
    setCostCenter('');
    setStatusMsg(null);
  };

  const handleAddItem = () => {
    if (!canSubmitSingle) return;

    const responsible = people.find(p => String(p.id) === String(responsibleId));

    const newItem = {
      exitType,
      warehouseId,
      materialId,
      quantity,
      reason: reason || "Consumible", // Default reason if empty
      observations,
      serial: serialOption === 'Sí' ? serial.trim() : '',
      brandName: selectedBrand || '',
      responsibleId,
      responsibleName: responsible?.name || '',
      responsibleDoc: responsible?.dni || responsible?.id || '',
      evidencePhotos,
      costCenter
    };
    setItems(prev => [...prev, newItem]);
    setQuantity(0);
    setSerial('');
    setSelectedBrand('');
    setEvidencePhotos([]);
    // Keep responsible and warehouse for next item usually
    setStatusMsg('Artículo agregado.');
  };

  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const responsible = people.find(p => String(p.id) === String(responsibleId));
    
    // Si hay artículos acumulados, registrar todos; si no, registrar único
    const batch = items.length ? items : [{
      exitType,
      warehouseId,
      materialId,
      quantity,
      reason: reason || "Consumible",
      observations,
      serial: serialOption === 'Sí' ? serial.trim() : '',
      brandName: selectedBrand || '',
      responsibleId,
      responsibleName: responsible?.name || '',
      responsibleDoc: responsible?.dni || responsible?.id || '',
      evidencePhotos,
      costCenter
    }];

    let okCount = 0;
    let lastMsg = '';
    let lastItem = null;
    for (const it of batch) {
      const res = await onRegister(it);
      lastMsg = res?.message || '';
      if (res?.success) okCount += 1;
      lastItem = it; // guarda el último ítem exitoso
    }
    setStatusMsg(okCount === batch.length ? (lastMsg || 'Operación realizada') : 'Algunos elementos no se registraron.');
    if (okCount === batch.length) {
      setItems([]);
      // Construir comprobante y disparar impresión automáticamente
      try {
        const material = materials.find(m => String(m.id) === String(lastItem.materialId));
        const wh = warehouses.find(w => String(w.id) === String(lastItem.warehouseId));
        
        // Mapear todos los ítems del lote para el recibo
        const receiptItems = batch.map(it => {
          const mat = materials.find(m => String(m.id) === String(it.materialId));
          return {
            materialName: mat?.name || String(it.materialId),
            materialCode: mat?.code,
            materialUnit: mat?.unit,
            category: mat?.category || mat?.type || 'Material',
            quantity: parseInt(it.quantity),
            serial: it.serial || '',
            brandName: it.brandName || ''
          };
        });

        const receipt = {
          type: 'Salida - ' + (lastItem.exitType || ''),
          date: new Date().toLocaleString('es-CO', { timeZone: 'America/Bogota' }),
          materialName: material?.name || String(lastItem.materialId),
          materialCode: material?.code,
          materialUnit: material?.unit,
          materialLocation: material?.location,
          materialStatus: material?.status,
          category: material?.category || material?.type || 'Material',
          quantity: parseInt(lastItem.quantity),
          reason: lastItem.reason,
          observations: lastItem.observations || '',
          serial: lastItem.serial || '',
          brandName: lastItem.brandName || '',
          warehouseId: lastItem.warehouseId || wh?.id || 'popayan',
          warehouseName: wh?.name,
          responsibleDoc: lastItem.responsibleDoc || 'Sistema',
          responsibleName: lastItem.responsibleName || '',
          evidencePhotos: lastItem.evidencePhotos,
          items: receiptItems
        };
        setLastReceipt(receipt);
        printMovement(receipt);
      } catch (e) {}
      handleCancel();
    }
  };

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <h3 className="section-title mb-6">Consumibles</h3>

      {lastReceipt && (
        <div className="mb-4 p-3 border rounded bg-gray-50 flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold">Movimiento registrado: {lastReceipt.type}</p>
            <p className="text-xs text-gray-600">{lastReceipt.materialName} — Cant: {lastReceipt.quantity} — {lastReceipt.date}</p>
          </div>
          <button type="button" onClick={() => printMovement(lastReceipt)} className="bg-gray-800 text-white px-3 py-2 rounded flex items-center gap-2">
            <Printer size={16} /> Imprimir comprobante
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Row 1 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Salida *</label>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500 bg-gray-100"
              value={exitType}
              onChange={(e) => setExitType(e.target.value)}
              disabled
            >
              {exitTypes.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Bodega *</label>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
              value={warehouseId}
              onChange={(e) => setWarehouseId(e.target.value)}
            >
              {warehouses.map((w) => (
                <option key={w.id} value={w.id}>{w.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Material *</label>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
              value={materialId}
              onChange={(e) => setMaterialId(e.target.value)}
            >
              <option value="">Seleccione...</option>
              {filteredMaterials.map((m) => {
                const stock = m.stockByWarehouse?.[warehouseId] || 0;
                return (
                  <option key={m.id} value={m.id}>
                    {m.code ? `${m.code} - ${m.name} (Stock: ${stock})` : `${m.id} - ${m.name} (Stock: ${stock})`}
                  </option>
                );
              })}
            </select>
          </div>
        </div>

        {/* Row 2 */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-start">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Cantidad *</label>
            <input
              type="number"
              min={0}
              value={quantity}
              onChange={(e) => setQuantity(Number(e.target.value))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
              placeholder="0"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Responsable *</label>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
              value={responsibleId}
              onChange={(e) => setResponsibleId(e.target.value)}
            >
              <option value="">Seleccione responsable...</option>
              {people.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Centro de costo *</label>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
              value={costCenter}
              onChange={(e) => setCostCenter(e.target.value)}
            >
              <option value="">Seleccione...</option>
              {costCenters.map((cc) => (
                <option key={cc.id} value={cc.code || cc.name}>{cc.code ? `${cc.code} - ${cc.name}` : cc.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Motivo</label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
              placeholder="Ej: Consumo interno"
            />
          </div>
        </div>

        {/* Row 3 - Serial/Brand */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border rounded-lg p-3 bg-gray-50">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Serie</label>
                  <select
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    value={serialOption}
                    onChange={(e) => setSerialOption(e.target.value)}
                  >
                    <option value="No">No</option>
                    <option value="Sí">Sí</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Marca</label>
                  <select
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    value={selectedBrand}
                    onChange={(e) => setSelectedBrand(e.target.value)}
                  >
                    <option value="">(Opcional)</option>
                    {brandOptions.map((b) => (
                      <option key={b.id} value={b.name}>{b.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              {serialOption === 'Sí' && (
                <div className="mt-3">
                  <input
                    type="text"
                    value={serial}
                    onChange={(e) => setSerial(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    placeholder="Ingrese la serie"
                  />
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Observaciones</label>
              <textarea
                value={observations}
                onChange={(e) => setObservations(e.target.value)}
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                placeholder="Detalles adicionales..."
              />
            </div>
        </div>

        {/* Photo Evidence */}
        <div className="border rounded-lg p-4 bg-gray-50">
          <PhotoEvidenceModule
            photos={evidencePhotos}
            onPhotosChange={setEvidencePhotos}
          />
        </div>

        {/* Acciones */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleAddItem}
            disabled={!canSubmitSingle}
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg disabled:opacity-50"
          >
            <Plus className="h-4 w-4" /> Agregar artículo
          </button>
          <button
            type="button"
            onClick={handleCancel}
            className="inline-flex items-center gap-2 bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-lg"
          >
            <X className="h-4 w-4" /> Cancelar
          </button>
          <button
            type="submit"
            disabled={!canSubmitSingle && items.length === 0}
            className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg disabled:opacity-50"
          >
            <Save className="h-4 w-4" /> Guardar
          </button>
        </div>

        {/* Lista de artículos */}
        {items.length > 0 && (
          <div className="mt-4 border rounded-lg">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-100">
                  <th className="p-2 text-left">Bodega</th>
                  <th className="p-2 text-left">Material</th>
                  <th className="p-2 text-left">Responsable</th>
                  <th className="p-2 text-left">Centro de costo</th>
                  <th className="p-2 text-left">Cant.</th>
                  <th className="p-2 text-left">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => {
                  const m = materials.find(x => String(x.id) === String(it.materialId));
                  const w = warehouses.find(x => String(x.id) === String(it.warehouseId));
                  return (
                    <tr key={idx} className="border-t">
                      <td className="p-2">{w?.name || it.warehouseId}</td>
                      <td className="p-2">{m?.name || it.materialId}</td>
                      <td className="p-2">{it.responsibleName}</td>
                      <td className="p-2">{it.costCenter || ''}</td>
                      <td className="p-2">{it.quantity}</td>
                      <td className="p-2">
                        <button
                          type="button"
                          onClick={() => removeItem(idx)}
                          className="inline-flex items-center gap-1 text-red-600 hover:text-red-800"
                        >
                          <Trash className="h-4 w-4" /> Quitar
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Nota de estado */}
        {statusMsg && (
          <p className="text-sm text-gray-600 pt-2">{statusMsg}</p>
        )}
      </form>
    </div>
  );
}
